import { Badge, Button, Card, CardHeader, EmptyState } from "@helmr/ui";
import { ENTITY_MAP, templateCsv, type EntityName } from "@helmr/importer";
import { useMutation } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { uploadSheet, type ImportJob } from "../../api/imports";
import { useServices } from "../../app/services";
import s from "../settings/settings.module.css";
import { downloadText } from "./csv";
import { SheetError, readSheet } from "./sheet";
import { StepError, useImportScope } from "./shared";
import { t } from "./strings";

const ENTITIES: EntityName[] = ["members", "memberships", "dues", "pt_balances", "staff", "leads", "device_users"];

/** Step 1: pick what the sheet holds, take the template if wanted, upload. Several sheets are allowed (one per kind of data). */
export function UploadStep({ job, onReload, onUploaded, onContinue }: { job: ImportJob; onReload: () => void; onUploaded: () => void; onContinue: () => void }) {
  const { client } = useServices();
  const sc = useImportScope(job.id);
  const [entity, setEntity] = useState<EntityName>("members");
  const [localError, setLocalError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const upload = useMutation({
    mutationFn: async (file: File) => uploadSheet(client, job.id, { filename: file.name, entity, rows: await readSheet(file) }),
    onSuccess: (next) => { setLocalError(null); sc.putJob(next); onUploaded(); },
    onError: (e) => { if (e instanceof SheetError) setLocalError(e.message); },
  });
  const pick = (file: File | undefined) => { setLocalError(null); if (file) upload.mutate(file); if (input.current) input.current.value = ""; };

  return (
    <Card aria-label={t.steps.upload.title}>
      <CardHeader title={t.steps.upload.title} subtitle={t.steps.upload.body} />
      <div className={s.fields}>
        <div className={`${s.field} ${s.full}`}>
          <span className={s.lab} id="entity-label">What does this sheet hold?</span>
          <div role="radiogroup" aria-labelledby="entity-label" className={s.checks}>
            {ENTITIES.map((e) => (
              <label key={e} className={s.check}>
                <input type="radio" name="entity" checked={entity === e} onChange={() => setEntity(e)} /> {t.entities[e]}
              </label>
            ))}
          </div>
          <span className={s.hint}>{t.entityHint[entity]}</span>
        </div>
        <div className={`${s.acts} ${s.full}`}>
          <Button onClick={() => downloadText(`helmr-${entity}-template.csv`, templateCsv(entity))}>{t.template}</Button>
          <Button variant="primary" busy={upload.isPending} busyLabel={t.uploading} onClick={() => input.current?.click()}>{t.upload}</Button>
          <input ref={input} type="file" accept=".csv,.txt,.tsv,.xlsx,.xls,.xlsm" hidden aria-label="Sheet file" onChange={(e) => pick(e.target.files?.[0])} />
        </div>
        <span className={`${s.hint} ${s.full}`}>The template lists {ENTITY_MAP[entity].fields.length} columns: {ENTITY_MAP[entity].fields.filter((f) => f.required).map((f) => f.label).join(", ") || "none"} required.</span>
        {localError ? <div className={`${s.formErr} ${s.full}`} role="alert" style={{ margin: 0 }}>{localError}</div> : null}
        <div className={s.full}><StepError error={upload.error instanceof SheetError ? null : upload.error} onReload={onReload} /></div>
      </div>
      {job.files.length === 0 ? (
        <EmptyState compact icon="receipt" title={t.noSheets} description={t.empty.body} />
      ) : (
        <div role="list" aria-label="Sheets in this import">
          {job.files.map((f) => (
            <div key={f.id} role="listitem" className={s.li}>
              <span><b className={s.name}>{f.filename}</b><small className={s.sub}>{t.entities[f.entity]} · {t.rows(f.rowCount)}</small></span>
              <Badge tone={job.mappedFileIds.includes(f.id) ? "ok" : "neutral"}>{job.mappedFileIds.includes(f.id) ? "Columns matched" : "Columns not matched"}</Badge>
            </div>
          ))}
        </div>
      )}
      <div className={s.bar}>
        <span className={s.hint}>An import takes up to 20 sheets, 5 MB and 20,000 rows each.</span>
        <Button variant="primary" disabled={job.files.length === 0} onClick={onContinue}>Continue to columns</Button>
      </div>
    </Card>
  );
}
